/**
 * Developer Onboarding Orchestrator
 *
 * This orchestrator onboards developers by:
 * 1. Retrieving/creating user(s) in current environment
 * 2. Ensuring organization exists (or using existing one)
 * 3. Setting organization configuration
 * 4. Ensuring application exists (or using existing one)
 * 5. Ensuring invite template is deployed to Passport for the app
 * 6. Adding users to "App Developers" group (sends invite email)
 * 7. Adding users to "Enabled Developers" group (optional)
 * 8. Finalizing onboarding
 *
 * The orchestrator runs in a single environment (PROD or SANDBOX).
 * Environment is determined by the API URL configured in the environment.
 *
 * Business logic is in developer_onboarding_shared.mjs
 * Server uses _userType + _scriptName to find and execute the correct function
 */

// =============================================================================
// ORCHESTRATOR DEFINITION
// =============================================================================

/**
 * Create the Developer Onboarding Orchestrator
 *
 * Input: {
 *   permissionProfileId: "abc-123-def" // Optional - if not provided, orchestrator will run with user's permissions
 * }
 *
 * Example usage:
 * const orchestrator = await createDeveloperOnboardingOrchestrator({ permissionProfileId: "..." }, libraries, ctx);
 *
 * Then run it with:
 * const result = await IafDataSource.runOrchestrator({
 *   orchestratorId: orchestrator.id,
 *   params: {
 *     // Organization selection/creation
 *     organizationId: "existing-org-id",      // Use existing org (if provided, orgName/shortName ignored)
 *     organizationName: "Acme Corp",          // For creating new org
 *     organizationShortName: "acme_corp",     // For creating new org (optional, auto-generated if not provided)
 *
 *     // Application selection/creation
 *     applicationId: "existing-app-id",       // Use existing app (if provided, appName/shortName ignored)
 *     applicationName: "Training Application",// For creating new app
 *     applicationShortName: "training_app",   // For creating new app
 *
 *     // Users to onboard
 *     adminEmail: "admin@acmecorp.com",       // Required if creating org/app (becomes owner)
 *     developerEmails: ["dev1@acmecorp.com", "dev2@acmecorp.com"], // Additional developers to add
 *
 *     // Options
 *     dryRun: false,
 *
 *     // Invite email customization (maps to Passport template body_content etc.)
 *     inviteLink: "",
 *     inviterName: "",
 *     bodyContent: "",
 *
 *     // Skip flags (for environment-specific behavior)
 *     skipRetrieveUser: false,
 *     skipEnsureOrganization: false,
 *     skipSetOrgConfig: false,
 *     skipEnsureApp: false,
 *     skipAddToAppDevs: false,
 *     skipAddToEnabledDevs: false,  // Useful in PROD where this group may not exist
 *
 *     // Privileged Console App token + management app context
 *     consoleAuthToken: "...",
 *     managementAppId: "...",
 *     currentUserEmail: "runner@example.com"
 *   }
 * }, ctx);
 */
/**
 * Build the canonical Developer Onboarding Orchestrator definition.
 *
 * Used by both `createDeveloperOnboardingOrchestrator` and
 * `updateOrchestratorPermissionProfile` so the shape sent to
 * `IafDataSource.updateOrchestrator` is identical to the one used at
 * creation time. The shape returned by `getOrchestratorBasedOnUserType`
 * (with `id` / `orchsteps` / etc.) is NOT accepted as an update payload,
 * so we always rebuild from this template.
 */
function buildDeveloperOnboardingOrchestratorDef(ctx, { permissionProfileId } = {}) {
    const orchestratorDef = {
        _name: "Developer Onboarding Orchestrator",
        _description: "Onboards developers by setting up organization, application, and user groups in the current environment",
        _namespaces: ctx._namespaces,
        _userType: "developer_onboarding",
        _instant: true,
        _params: {
            tasks: [
                {
                    _sequenceno: 1,
                    _orchcomp: 'default_script_target',
                    _name: "retrieve_user",
                    _actualparams: {
                        userType: 'onboarding_shared',
                        _scriptName: "retrieveUser"
                    }
                },
                {
                    _sequenceno: 2,
                    _orchcomp: 'default_script_target',
                    _name: "ensure_organization",
                    _actualparams: {
                        userType: 'onboarding_shared',
                        _scriptName: "ensureOrganization"
                    }
                },
                {
                    _sequenceno: 3,
                    _orchcomp: 'default_script_target',
                    _name: "set_org_config",
                    _actualparams: {
                        userType: 'onboarding_shared',
                        _scriptName: "setOrganizationConfig"
                    }
                },
                {
                    _sequenceno: 4,
                    _orchcomp: 'default_script_target',
                    _name: "ensure_training_app",
                    _actualparams: {
                        userType: 'onboarding_shared',
                        _scriptName: "ensureApplication"
                    }
                },
                {
                    _sequenceno: 5,
                    _orchcomp: 'default_script_target',
                    _name: "ensure_invite_template",
                    _actualparams: {
                        userType: 'onboarding_shared',
                        _scriptName: "ensureInviteTemplate"
                    }
                },
                {
                    _sequenceno: 6,
                    _orchcomp: 'default_script_target',
                    _name: "add_user_to_app_devs",
                    _actualparams: {
                        userType: 'onboarding_shared',
                        _scriptName: "addUsersToAppDevelopers"
                    }
                },
                {
                    _sequenceno: 7,
                    _orchcomp: 'default_script_target',
                    _name: "add_user_to_enabled_devs",
                    _actualparams: {
                        userType: 'onboarding_shared',
                        _scriptName: "addUsersToEnabledDevelopers"
                    }
                },
                {
                    _sequenceno: 8,
                    _orchcomp: 'default_script_target',
                    _name: "finalize_onboarding",
                    _actualparams: {
                        userType: 'onboarding_shared',
                        _scriptName: "finalizeOnboarding"
                    }
                }
            ]
        }
    };

    if (permissionProfileId) {
        orchestratorDef._permissionprofileid = permissionProfileId;
    }

    return orchestratorDef;
}

async function createDeveloperOnboardingOrchestrator(input, libraries, ctx) {
    const { IafDataSource } = libraries.PlatformApi;

    const { permissionProfileId } = input || {};

    // Always start from a clean slate: delete any existing orchestrators
    // for this _userType before creating a new one, so we never end up
    // with two definitions side by side (which would also cause
    // `getOrchestratorBasedOnUserType(...)[0]` to be ambiguous).
    let deletedExisting = [];
    try {
        const existing = await IafDataSource.getOrchestratorBasedOnUserType("developer_onboarding", ctx);
        for (const orch of existing || []) {
            const orchId = orch.id || orch._id;
            if (!orchId) continue;
            try {
                await IafDataSource.deleteOrchestrator(orchId, ctx);
                deletedExisting.push({ orchestratorId: orchId, orchestratorName: orch._name });
            } catch (delErr) {
                console.warn(
                    `Could not delete existing orchestrator ${orchId} (${orch._name}):`,
                    delErr?.errorResult?.errorMessage || delErr?.message || delErr
                );
            }
        }
    } catch (lookupErr) {
        console.warn(
            "Could not look up existing developer_onboarding orchestrators before create:",
            lookupErr?.errorResult?.errorMessage || lookupErr?.message || lookupErr
        );
    }

    const orchestratorDef = buildDeveloperOnboardingOrchestratorDef(ctx, { permissionProfileId });

    const result = await IafDataSource.createOrchestrator(orchestratorDef, ctx);
    return {
        ...result,
        deletedExisting
    };
}

/**
 * Run the Developer Onboarding Orchestrator
 *
 * Input: {
 *   orchestratorUserType: "developer_onboarding",
 *
 *   // Organization - either select existing or create new
 *   organizationId: "existing-org-id",        // Optional - if provided, uses existing org
 *   organizationName: "Acme Corp",            // Required if creating new org
 *   organizationShortName: "acme_corp",       // Optional for new org (auto-generated)
 *
 *   // Application - either select existing or create new
 *   applicationId: "existing-app-id",         // Optional - if provided, uses existing app
 *   applicationName: "Training Application",  // Required if creating new app
 *   applicationShortName: "training_app",     // Required if creating new app
 *
 *   // Users
 *   adminEmail: "admin@acmecorp.com",         // Required if creating org/app
 *   developerEmails: ["dev1@acmecorp.com"],   // Optional - additional developers to add
 *
 *   // Options
 *   dryRun: false,
 *
 *   // Invite email customization (maps to Passport template body_content etc.)
 *   inviteLink: "",
 *   inviterName: "",
 *   bodyContent: "",
 *
 *   // Skip flags
 *   skipRetrieveUser: false,
 *   skipEnsureOrganization: false,
 *   skipSetOrgConfig: false,
 *   skipEnsureApp: false,
 *   skipAddToAppDevs: false,
 *   skipAddToEnabledDevs: false,
 *
 *   // Privileged Console App token + management app context
 *   consoleAuthToken: "...",
 *   managementAppId: "...",
 *   currentUserEmail: "runner@example.com"
 * }
 */
async function runDeveloperOnboardingOrchestrator(input, libraries, ctx) {
    const { IafDataSource } = libraries.PlatformApi;

    const {
        orchestratorUserType,
        // Organization
        organizationId,
        newOrganizationId,
        organizationName,
        organizationShortName = "",
        // Application
        applicationId,
        newApplicationId,
        applicationName,
        applicationShortName,
        // Users
        adminEmail,
        developerEmails = [],
        // Options
        dryRun = false,
        // Invite email customization
        inviteLink = "",
        inviterName = "",
        bodyContent = "",
        // Skip flags
        skipRetrieveUser = false,
        skipEnsureOrganization = false,
        skipSetOrgConfig = false,
        skipEnsureApp = false,
        skipAddToAppDevs = false,
        skipAddToEnabledDevs = false,
        // Privileged token and management app context
        consoleAuthToken,
        managementAppId,
        currentUserEmail
    } = input || {};

    // Find the orchestrator by userType
    const orchestrators = await IafDataSource.getOrchestratorBasedOnUserType(orchestratorUserType || "developer_onboarding", ctx);

    if (!orchestrators || orchestrators.length === 0) {
        return {
            success: false,
            message: "Orchestrator not found. Please create it first using createDeveloperOnboardingOrchestrator."
        };
    }

    const orchestrator = orchestrators[0];

    let orchReqs = {
        orchestratorId: orchestrator.id,
        _actualparams: [{
            sequence_type_id: orchestrator.orchsteps[0]._compid,
            params: {
                // Organization
                organizationId,
                newOrganizationId,
                organizationName,
                organizationShortName,
                // Application
                applicationId,
                newApplicationId,
                applicationName,
                applicationShortName,
                // Users
                adminEmail,
                developerEmails,
                // Options
                dryRun,
                // Invite email customization
                inviteLink,
                inviterName,
                bodyContent,
                // Skip flags
                skipRetrieveUser,
                skipEnsureOrganization,
                skipSetOrgConfig,
                skipEnsureApp,
                skipAddToAppDevs,
                skipAddToEnabledDevs,
                // Privileged token and management app context
                consoleAuthToken,
                managementAppId,
                currentUserEmail
            }
        }]
    };

    // Run the orchestrator with parameters
    const runResult = await IafDataSource.runOrchestrator(orchestrator.id, orchReqs, ctx);

    return {
        success: true,
        orchestratorId: orchestrator._id,
        runId: runResult._id,
        status: runResult._status,
        message: "Developer onboarding orchestrator started successfully"
    };
}

/**
 * Update permission profile attachment on existing orchestrator
 *
 * Input: {
 *   permissionProfileId: "abc-123-def" // Required - the profile ID to attach
 * }
 */
async function updateOrchestratorPermissionProfile(input, libraries, ctx) {
    const { IafDataSource } = libraries.PlatformApi;

    const { permissionProfileId } = input || {};

    if (permissionProfileId === undefined) {
        return {
            success: false,
            error: "permissionProfileId is required (pass null to remove)"
        };
    }

    // Find the orchestrator
    const orchestrators = await IafDataSource.getOrchestratorBasedOnUserType("developer_onboarding", ctx);

    if (!orchestrators || orchestrators.length === 0) {
        return {
            success: false,
            error: "Orchestrator not found. Please create it first."
        };
    }

    const orchestrator = orchestrators[0];

    // `getOrchestratorBasedOnUserType` returns the server-side shape (with
    // `id` and `orchsteps`), which is NOT a valid `updateOrchestrator`
    // payload. Use it only to look up the id + previous profile, and
    // rebuild the def in the same shape `createOrchestrator` accepts.
    const orchestratorId = orchestrator.id || orchestrator._id;

    if (!orchestratorId) {
        return {
            success: false,
            error: "Could not determine orchestrator id from loaded orchestrator"
        };
    }

    const previousProfileId = orchestrator._permissionprofileid || null;

    const orchestratorDef = buildDeveloperOnboardingOrchestratorDef(ctx, { permissionProfileId });

    console.log('Updating orchestrator with permission profile:', { orchestratorId, orchestratorDef, permissionProfileId });

    const updatedOrch = await IafDataSource.updateOrchestrator(orchestratorId, orchestratorDef, ctx);

    return {
        success: true,
        orchestratorId,
        orchestratorName: orchestratorDef._name,
        permissionProfileId,
        previousProfileId,
        updatedOrch,
        message: previousProfileId
            ? "Permission profile updated successfully"
            : "Permission profile attached successfully"
    };
}

/**
 * Delete the Developer Onboarding Orchestrator
 */
async function deleteDeveloperOnboardingOrchestrator(input, libraries, ctx) {
    const { IafDataSource } = libraries.PlatformApi;

    const orchestrators = await IafDataSource.getOrchestratorBasedOnUserType("developer_onboarding", ctx);

    const results = [];
    for (const orchestrator of orchestrators || []) {
        if (orchestrator.id) {
            await IafDataSource.deleteOrchestrator(orchestrator.id, ctx);
            results.push({
                deleted: true,
                orchestratorId: orchestrator.id,
                orchestratorName: orchestrator._name
            });
        }
    }

    return results;
}

/**
 * Get orchestrator run status and results
 */
async function getDeveloperOnboardingOrchestratorResult(input, libraries, ctx) {
    const { IafDataSource } = libraries.PlatformApi;

    const { orchestratorRunId } = input || {};

    const status = await IafDataSource.getOrchestratorRunStatus(orchestratorRunId, ctx);

    return {
        orchestratorRunId: orchestratorRunId,
        status: status._status,
        result: status
    };
}


const scriptModule = {
    createDeveloperOnboardingOrchestrator,
    runDeveloperOnboardingOrchestrator,
    updateOrchestratorPermissionProfile,
    deleteDeveloperOnboardingOrchestrator,
    getDeveloperOnboardingOrchestratorResult
}

export default scriptModule;
